import {select, fork, take, call, put} from 'redux-saga/effects';
import {matchPath} from '@computerrock/formation-router';
import {persistenceStates, TemporaryRestriction, efDamageNodeTypes, efServiceTypes, apmContractPartnerContractStatusTypes, apmACEPartnerTypes, ampVehicleTypes, apmContractPartnerVehicleCompatibilityTypes, ampVehicleFuelTypes, ACEPartner, efServiceCaseTypes, efServiceAssignmentTypes} from '@ace-de/eua-entity-types';
import fetchRequest from '../../application/sagas/fetchRequest';
import config from '../../config';
import * as serviceAssignmentActionTypes from '../serviceAssignmentActionTypes';
import isTemporaryRestrictionActive from '../../contract-partners/isTemporaryRestrictionActive';
import isACEPartnerFromDACH from '../isACEPartnerFromDACH';
import updateServiceCase from '../../service-cases/sagas/updateServiceCase';
import updateServiceAssignment from './updateServiceAssignment';
import * as serviceCaseHelpers from '../../service-cases/sagas/serviceCaseHelpers';
import * as contractPartnerActionTypes from '../../contract-partners/contractPartnerActionTypes';
import routePaths from '../../routePaths';

const createDamageDescriptionString = (damageNodeSnapshots, damageNodes, translate, translateToEnglish) => {
    let snapshotIds = [...damageNodeSnapshots];
    const description = [];
    damageNodeSnapshots.forEach(nodeId => {
        if (!snapshotIds.includes(nodeId)) return;
        const node = damageNodes[nodeId];
        if (!node) return;
        if (node.nodeType === efDamageNodeTypes.ANSWER) {
            translateToEnglish ? description.push(translate(node.name, {}, 'en-US'))
                : description.push(translate(node.name));
            description.push('\n');
            return;
        }

        if (node.nodeType === efDamageNodeTypes.DECISION) {
            const childrenInSnapshot = node.childNodeIds.filter(childId => damageNodeSnapshots.includes(childId));
            if (childrenInSnapshot.length) {
                translateToEnglish ? description.push(translate(node.name, {}, 'en-US'), ' ')
                    : description.push(translate(node.name), ' ');
                childrenInSnapshot.forEach((childrenId, idx) => {
                    translateToEnglish ? description.push(translate(damageNodes[childrenId].name, {}, 'en-US'))
                        : description.push(translate(damageNodes[childrenId].name));
                    if (idx !== childrenInSnapshot.length - 1) {
                        description.push('***');
                    }
                    snapshotIds = snapshotIds.filter(nodeId => nodeId !== childrenId);
                });
                description.push('\n');
            }
        }
    });
    return description.toString().replaceAll(',', '').replaceAll('***', ', ');
};

const filterContractPartnersByLocation = function* filterContractPartnersByLocation(
    serviceCaseId,
    serviceAssignmentLineNo,
) {
    const {serviceManager} = yield select(state => state.application);
    const partnerManagementService = serviceManager.loadService('partnerManagementService');
    const googleMapsService = serviceManager.loadService('googleMapsService');
    const i18nService = serviceManager.loadService('i18nService');
    const {translate} = i18nService;
    const {serviceCases} = yield select(state => state.serviceCases);
    const {serviceAssignments} = yield select(state => state.serviceAssignments);
    const {contractPartners} = yield select(state => state.contractPartners);
    const {location} = yield select(state => state.router);
    const serviceCase = serviceCases[serviceCaseId];
    const serviceAssignmentId = `${serviceCaseId}-${serviceAssignmentLineNo}`;
    const serviceAssignment = serviceAssignments[serviceAssignmentId];
    const sapRouteMatch = matchPath(location?.pathname, {
        path: routePaths.SERVICE_ASSIGNMENT_PICKUP,
        exact: true,
    });
    const savRouteMatch = matchPath(location?.pathname, {
        path: routePaths.SERVICE_ASSIGNMENT_VEHICLE,
        exact: true,
    });

    if (!serviceAssignment || !serviceCase.damage || !serviceCase.damage.location) return;

    yield put({
        type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
        payload: {persistenceState: persistenceStates.PENDING},
    });

    yield put({
        type: serviceAssignmentActionTypes.SET_CONTRACT_PARTNER_RECOMMENDATIONS,
        payload: {serviceAssignmentId, contractPartnerRecommendationDTOs: []},
    });

    yield* serviceCaseHelpers.setPersistencePending(serviceCaseId);

    const isInitialAssignment = serviceAssignment.lineNo === 'FBS1'
        || parseInt(serviceAssignment.lineNo, 10) === 1
        || serviceAssignment.isCloneFromInitial;
    const damageLocation = isInitialAssignment
        ? serviceCase.damage.location
        : serviceAssignment.serviceLocation;
    const {pickupLocation, destination} = serviceAssignment;

    if ((!sapRouteMatch && !damageLocation) || (sapRouteMatch && (!pickupLocation || !destination))) return;

    const searchQueryParams = new URLSearchParams();
    searchQueryParams.append('latitude', sapRouteMatch ? pickupLocation.latitude : damageLocation.latitude);
    searchQueryParams.append('longitude', sapRouteMatch ? pickupLocation.longitude : damageLocation.longitude);
    searchQueryParams.append('countOfNearby', config.CONTRACT_PARTNER_RECOMMENDATION_COUNT.toString());
    searchQueryParams.append('contractStatus', apmContractPartnerContractStatusTypes.ACTIVE);
    searchQueryParams.append('partnerType', apmACEPartnerTypes.CONTRACT_PARTNER);

    yield fork(
        fetchRequest,
        serviceAssignmentActionTypes.FIND_NEAREST_CONTRACT_PARTNERS_BY_LOCATION_REQUEST,
        partnerManagementService.findNearestContractPartner,
        {searchQueryParams},
    );

    const apmResponseAction = yield take([
        serviceAssignmentActionTypes.FIND_NEAREST_CONTRACT_PARTNERS_BY_LOCATION_REQUEST_SUCCEEDED,
        serviceAssignmentActionTypes.FIND_NEAREST_CONTRACT_PARTNERS_BY_LOCATION_REQUEST_FAILED,
    ]);

    if (apmResponseAction.error) {
        yield put({
            type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
            payload: {persistenceState: persistenceStates.FAILED},
        });
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {response: apmResponse} = apmResponseAction.payload;
    const {acePartnerDTOs} = apmResponse;

    if (!acePartnerDTOs.length) {
        yield put({
            type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
            payload: {persistenceState: persistenceStates.READY},
        });
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {acePartnerDTOs: acePartnerDTOsWithLocations} = yield call(partnerManagementService.getACEPartnersById, {
        acePartnerIds: acePartnerDTOs
            .map(acePartnerDTO => acePartnerDTO['id'])
            .filter(Boolean),
    });

    yield put({
        type: contractPartnerActionTypes.STORE_CONTRACT_PARTNERS,
        payload: {contractPartnerDTOs: acePartnerDTOsWithLocations},
    });

    yield fork(
        fetchRequest,
        serviceAssignmentActionTypes.SORT_CONTRACT_PARTNERS_BY_DISTANCE_REQUEST,
        googleMapsService.sortDistances,
        {
            origin: {
                location: {
                    latLng: {
                        latitude: sapRouteMatch ? pickupLocation.latitude : damageLocation.latitude,
                        longitude: sapRouteMatch ? pickupLocation.longitude : damageLocation.longitude,
                    },
                },
            },
            destinations: acePartnerDTOsWithLocations.map(acePartnerDTO => ({
                apiUnrelatedFields: acePartnerDTO,
                location: {
                    latLng: {
                        latitude: acePartnerDTO.location.latitude,
                        longitude: acePartnerDTO.location.longitude,
                    },
                },
            })),
        },
    );

    const sortDistancesResponseAction = yield take([
        serviceAssignmentActionTypes.SORT_CONTRACT_PARTNERS_BY_DISTANCE_REQUEST_SUCCEEDED,
        serviceAssignmentActionTypes.SORT_CONTRACT_PARTNERS_BY_DISTANCE_REQUEST_FAILED,
    ]);

    if (sortDistancesResponseAction.error) {
        yield put({
            type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
            payload: {persistenceState: persistenceStates.FAILED},
        });
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {response: sortedCpResponse} = sortDistancesResponseAction.payload;
    const {distances} = sortedCpResponse;

    const contractPartnersWithPickupRouteData = {};

    if (sapRouteMatch) {
        yield fork(
            fetchRequest,
            serviceAssignmentActionTypes.GET_CONTRACT_PARTNERS_DISTANCE_TO_DESTINATION_LOCATION_REQUEST,
            googleMapsService.sortDistances,
            {
                origin: {
                    location: {
                        latLng: {
                            latitude: destination.latitude,
                            longitude: destination.longitude,
                        },
                    },
                },
                destinations: acePartnerDTOsWithLocations.map(acePartnerDTO => ({
                    apiUnrelatedFields: acePartnerDTO,
                    location: {
                        latLng: {
                            latitude: acePartnerDTO.location.latitude,
                            longitude: acePartnerDTO.location.longitude,
                        },
                    },
                })),
                primarySortingProperty: 'distanceMeters',
                secondarySortingProperty: 'duration',
            },
        );

        const distanceToDestinationLocationResponseAction = yield take([
            serviceAssignmentActionTypes.GET_CONTRACT_PARTNERS_DISTANCE_TO_DESTINATION_LOCATION_REQUEST_SUCCEEDED,
            serviceAssignmentActionTypes.GET_CONTRACT_PARTNERS_DISTANCE_TO_DESTINATION_LOCATION_REQUEST_FAILED,
        ]);

        if (distanceToDestinationLocationResponseAction.error) return;

        const {response: distanceToDestinationLocationResponse} = distanceToDestinationLocationResponseAction.payload;
        const {distances: distancesToDestinationLocation} = distanceToDestinationLocationResponse;

        distancesToDestinationLocation.forEach(distanceToDestinationLocation => {
            const {id} = distanceToDestinationLocation.destination.apiUnrelatedFields;
            contractPartnersWithPickupRouteData[id] = distanceToDestinationLocation;
        });
    }

    const contractPartnerRecommendationDTOs = distances.map(distance => {
        const contractPartner = distance.destination.apiUnrelatedFields;
        const isServiceTypeBasicOne = [
            efServiceTypes.RECOVERY,
            efServiceTypes.ROADSIDE_ASSISTANCE,
            efServiceTypes.TOWING,
        ].includes(serviceCase.serviceType);
        const [service] = contractPartner?.services
            ? contractPartner.services.filter(service => service.serviceType === serviceCase.serviceType) : [];

        const additionalFields = {
            ...(service && {
                ...(isServiceTypeBasicOne && {
                    isVehicleOverweight: service.tonnageThreshold === config.MIN_ULTRA_HEAVY_WEIGHT_VEHICLE_TONNAGE
                        ? false
                        : (serviceCase?.vehicle?.weight
                            ? !((service.tonnageThreshold || 0) >= serviceCase.vehicle.weight)
                            : false),
                    isCamperIncompatible: !((service.camperDimensions
                            && serviceCase?.vehicle?.type === ampVehicleTypes.CAMPER
                            && Object.keys(service.camperDimensions).every(key => (
                                typeof service.camperDimensions[key] !== 'number'
                                || !service.camperDimensions[key]
                                || typeof serviceCase.vehicle[key] !== 'number'
                                || service.camperDimensions[key] >= serviceCase.vehicle[key])))
                        || !(!service.camperDimensions && serviceCase.vehicle?.type === ampVehicleTypes.CAMPER
                            && (serviceCase.vehicle?.weight || serviceCase.vehicle?.length
                                || serviceCase.vehicle?.width || serviceCase.vehicle?.height))),
                }),
                ...(serviceCase?.vehicle?.type === ampVehicleTypes.MOTORBIKE && {
                    isVehicleTypeIncompatible: !(contractPartner?.vehicleCompatibility?.length
                        ? contractPartner.vehicleCompatibility
                            .includes(apmContractPartnerVehicleCompatibilityTypes.MOTORBIKE)
                        : false),
                }),
                ...([ampVehicleFuelTypes.GAS, ampVehicleFuelTypes.HYBRID].includes(serviceCase?.vehicle?.fuelType)
                    && {
                        isVehicleFuelTypeIncompatible: !(serviceCase.vehicle.fuelType === ampVehicleFuelTypes.GAS
                            ? !!contractPartner?.vehicleCompatibility
                                ?.includes(apmContractPartnerVehicleCompatibilityTypes.CNG_LNG)
                            : !!contractPartner?.vehicleCompatibility
                                ?.includes(apmContractPartnerVehicleCompatibilityTypes.HYBRID)),
                    }),
            }),
        };

        return {
            ...contractPartner,
            routeToDamageLocation: {
                totalKilometers: distance.distanceMeters / 1000,
                totalTravelTime: Number(distance.duration.replace('s', '')) / 60,
            },
            ...(contractPartnersWithPickupRouteData[contractPartner.id] && {
                routeToPickupLocation: {
                    totalKilometers: contractPartnersWithPickupRouteData[contractPartner.id].distanceMeters / 1000,
                    totalTravelTime: Number(
                        contractPartnersWithPickupRouteData[contractPartner.id].duration.replace('s', ''),
                    ) / 60,
                },
            }),
            location: {
                latitude: contractPartner.location?.latitude,
                longitude: contractPartner.location?.longitude,
            },
            isServiceUnsupported: !service,
            ...(additionalFields),
            hasServiceArea: !!contractPartner.fixPricePolygon,
        };
    });

    if (contractPartnerRecommendationDTOs.length > 0) {
        const restrictionsMap = yield call(partnerManagementService.getACEPartnersRestrictions, {
            acePartnerIds: contractPartnerRecommendationDTOs
                .map(contractPartnerRecommendationDTO => contractPartnerRecommendationDTO.id)
                .filter(Boolean),
        });
        contractPartnerRecommendationDTOs.forEach(contractPartnerRecommendationDTO => {
            if (restrictionsMap && restrictionsMap[contractPartnerRecommendationDTO.id]) {
                const temporaryRestrictions = new Map();
                restrictionsMap[contractPartnerRecommendationDTO.id]
                    .forEach(restriction => {
                        const temporaryRestriction = new TemporaryRestriction().fromDTO(restriction);
                        if (isTemporaryRestrictionActive(temporaryRestriction)) {
                            temporaryRestrictions.set(temporaryRestriction.id, temporaryRestriction);
                        }
                    });
                contractPartnerRecommendationDTO.temporaryRestrictions = temporaryRestrictions;
            }
        });
    }

    let containingContractPartners = null;

    if (!sapRouteMatch) {
        const recommendedContractPartnerPolygons = googleMapsService.checkIsLocationInsidePolygons({
            longitude: damageLocation.longitude,
            latitude: damageLocation.latitude,
            polygons: contractPartnerRecommendationDTOs.map(acePartnerDTO => (
                acePartnerDTO.fixPricePolygon
            )).filter(Boolean),
        });
        containingContractPartners = contractPartnerRecommendationDTOs.filter(contractPartnerRecommendationDTO => (
            recommendedContractPartnerPolygons.includes(contractPartnerRecommendationDTO.fixPricePolygon)
        ));
    }

    const recommendedContractPartnerDTO = containingContractPartners?.[0] || contractPartnerRecommendationDTOs[0];

    const selectedContractPartner = serviceAssignment.acePartner?.isManuallyAdded
        ? serviceAssignment.acePartner
        : (serviceAssignment.acePartner?.id
            ? contractPartners[serviceAssignment.acePartner.id] || null
            : null);
    const previousPartners = serviceAssignment.listOfPreviousPartners || [];
    const selectedContractPartnerId = selectedContractPartner?.isManuallyAdded
        ? null
        : (selectedContractPartner
        && (selectedContractPartner.contractStatus === apmContractPartnerContractStatusTypes.ACTIVE)
            ? selectedContractPartner.id
            : (recommendedContractPartnerDTO
            && !(previousPartners?.length && previousPartners.includes(recommendedContractPartnerDTO.id))
                ? recommendedContractPartnerDTO.id : null));
    const isContractPartnerNotPreviouslySelected = !(previousPartners?.length
            && previousPartners.includes(recommendedContractPartnerDTO.id))
        && (!serviceAssignment.acePartner || (
            selectedContractPartnerId && selectedContractPartnerId !== selectedContractPartner?.id
        ));

    if (serviceAssignment.acePartner
        && !serviceAssignment.acePartner.isManuallyAdded
        && !contractPartnerRecommendationDTOs.find(contractPartnerRecommendationDTO => (
            selectedContractPartnerId && selectedContractPartnerId === contractPartnerRecommendationDTO.id
        ))) {
        contractPartnerRecommendationDTOs.push(serviceAssignment.acePartner);
    }

    // save recommended contract partner as selected if no contract partner was previously set
    if (recommendedContractPartnerDTO && isContractPartnerNotPreviouslySelected) {
        const recommendedContractPartner = new ACEPartner().fromDTO(recommendedContractPartnerDTO || null);
        const {damageNodeSnapshots} = serviceCase;

        if (damageNodeSnapshots.length > 0) {
            const isDACH = isACEPartnerFromDACH(recommendedContractPartner);
            const damageNodes = yield select(state => state.serviceCases.serviceTypeDamageNodes);
            const description = createDamageDescriptionString(damageNodeSnapshots, damageNodes, translate, !isDACH);

            yield* updateServiceCase({
                caller: sapRouteMatch
                    ? 'LOAD_SAP_CONTRACT_PARTNER_RECOMMENDATIONS'
                    : savRouteMatch
                        ? 'LOAD_SAV_CONTRACT_PARTNER_RECOMMENDATIONS'
                        : 'LOAD_SAVA_CONTRACT_PARTNER_RECOMMENDATIONS',
                caseType: efServiceCaseTypes.VEHICLE,
                serviceCaseId: serviceAssignment.serviceCaseId,
                serviceCaseData: {
                    damage: {
                        description: description,
                    },
                },
            });
        }

        yield* updateServiceAssignment({
            caller: sapRouteMatch
                ? 'LOAD_SAP_CONTRACT_PARTNER_RECOMMENDATIONS'
                : savRouteMatch
                    ? 'LOAD_SAV_CONTRACT_PARTNER_RECOMMENDATIONS'
                    : 'LOAD_SAVA_CONTRACT_PARTNER_RECOMMENDATIONS',
            assignmentType: sapRouteMatch
                ? efServiceAssignmentTypes.PICKUP
                : efServiceAssignmentTypes.VEHICLE,
            serviceAssignmentLineNo,
            serviceCaseId,
            serviceAssignmentData: {
                acePartner: {
                    ...recommendedContractPartner,
                    ...(recommendedContractPartner.businessContactDetails
                        ? {
                            businessContactDetails: {
                                ...recommendedContractPartner.businessContactDetails,
                                phoneNo: recommendedContractPartner?.emergencyContacts?.find(contact => contact.is24h7Emergency)?.phoneNo || '',
                            },
                            contactDetails: null,
                        } : {}
                    ),
                    rank: config.RECOMMENDED_CONTRACT_PARTNER_RANK, // recommended CP has always a rank of 1
                },
                assignmentText: null,
            },
        });

        // set persistence state back to PENDING
        yield* serviceCaseHelpers.setPersistencePending(serviceCaseId);
    }

    yield put({
        type: serviceAssignmentActionTypes.SET_CONTRACT_PARTNER_RECOMMENDATIONS,
        payload: {
            serviceAssignmentId,
            contractPartnerRecommendationDTOs,
            recommendedContractPartnerId: recommendedContractPartnerDTO?.id || null,
            // if several CPs have damage location inside their polygons (overlapping polygons)
            // we need them all as recommended CPs
            ...(containingContractPartners?.length > 1 && {
                recommendedContractPartnerIds: containingContractPartners.map(containingContractPartner => (
                    containingContractPartner.id
                )),
            }),
        },
    });

    yield put({
        type: serviceAssignmentActionTypes.SET_CP_RECOMMENDATIONS_PERSISTENCE_STATE,
        payload: {persistenceState: persistenceStates.READY},
    });

    const destinationContractPartner = isContractPartnerNotPreviouslySelected
        ? recommendedContractPartnerDTO
        : contractPartnerRecommendationDTOs.find(contractPartnerRecommendationDTO => (
            contractPartnerRecommendationDTO.id === selectedContractPartnerId
        ));

    yield put({
        type: serviceAssignmentActionTypes.INITIATE_CONTRACT_PARTNER_ROUTE_FETCH,
        payload: {
            originLocation: sapRouteMatch ? destination : damageLocation,
            destinationLocation: destinationContractPartner?.location,
            serviceAssignmentId,
            contractPartnerId: destinationContractPartner?.id,
        },
    });

    yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
};

export default filterContractPartnersByLocation;
